import React from 'react';
import {
  BloodworkAnalysis,
  FurtherConsiderations,
  PanelMarker,
  Section,
  RootCauseCategory,
} from '../../models';
import { AnemiaStatus } from './bloodwork-anemia-status';
import { ReferenceRangeAnalysisContainer } from '../../containers/bloodwork-ref-range-analysis-container';
import { WBCEvaluation } from './bloodwork-wbc-evaluation';
import { GlucoseRegulationInsights } from './glucose-regulation-insights';
import { NutrientInsights } from './nutrient-insights';
import { ThyroidRegulationInsights } from './thyroid-regulation-insights';
import { StressAdrenalRegulationInsights } from './stress-adrenal-regulation-insights';
import { DehydrationInsights, DEHYDRATION_PANEL_ENABLED } from './dehydration-insights';
// insightsPanelPrintable is the whole of this file's interest in clinical
// review: a panel pending it is screen-only, so the notice that accompanies it
// on screen has nothing to sit above here.
import { insightsPanelPrintable } from './insights-review-notice';
import {
  insightsSourceAnalysis,
  previousCollectionLabel,
  PREVIOUS_COLLECTION_CAPTION_PDF_HEIGHT,
} from '../../helpers/previousOnlyMarkers';
import { sectionTextMarkers } from '../../helpers/customTextMarkers';
import {
  BAR_GRAPH_BASE_HEIGHT,
  BAR_GRAPH_ROW_HEIGHT,
  BAR_GRAPH_TEXT_BLOCK_HEIGHT,
  BAR_GRAPH_TEXT_ROW_HEIGHT,
} from '../../helpers/pdfPanelMetrics';
import './previous-collection-insights.css';

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  title: string;
  section: Section;
  panelMarkers: PanelMarker[];
  /**
   * The practitioner's name for this section, when it is one of theirs. Every
   * such section prints as Section.CUSTOM_MARKERS, so without it the client's
   * PDF heads each one's results table with the same generic title.
   */
  practitionerSectionName?: string;
  /**
   * Suppress this section's text results on THIS chunk.
   *
   * Vitamin & Mineral Status and Hormone Balance print as two PDFSections when
   * they run past 13 markers, both carrying the same `section`. Text markers
   * are resolved FROM the section, so without this both chunks would print the
   * whole set and the client's report would list every urinalysis line twice.
   * Set on the first chunk, so the words land under the last of the charts —
   * the reading order the single-page sections already have.
   */
  hideTextMarkers?: boolean;
  /**
   * Suppress this section's Insights panel on THIS chunk.
   *
   * Vitamin & Mineral Status prints as two PDFSections when it runs past 13
   * markers, both carrying the same `section`, so without this the panel would
   * print once above each half. Set on the SECOND chunk: the panel belongs with
   * the first bars, the way it sits above them in every unsplit section.
   */
  hideInsightsPanel?: boolean;
  /**
   * Whether the page packer wants a page break AFTER this section.
   *
   * Emitted as this component's LAST element rather than as a class on the
   * wrapper the container puts around it. html2pdf's legacy pagebreak walks
   * `querySelectorAll('*')` — document order, so a parent is measured before
   * its children — and sizes each break's padding from the element's bottom
   * edge AT THAT MOMENT. A break on the outer wrapper was therefore measured
   * before the break INSIDE this section (the one that gives the results their
   * own page) had run, so its padding was short by most of a page and every
   * section that followed was displaced. Last-child ordering means both breaks
   * are measured after the inner one has taken effect.
   */
  breakAfter?: boolean;
};

/**
 * Whether `section` prints an Insights panel in this build, built from `ba`.
 *
 * The single source of truth for that question, which two places have to agree
 * on: evaluation() renders the panel, and render() sizes the page around it
 * (the duplicate-title predictor and the keep-together wrapper). While they
 * were separate lists, render() carried an RBC/WBC/GLUCOSE allowlist that was
 * never widened as panels were added, so Vitamins & Minerals, Thyroid, Stress &
 * Adrenal and Hydration each built a panel in evaluation() that render() never
 * asked for. None of the four reached the client's document.
 */
export function printsInsightsPanel(
  section: Section,
  bloodworkAnalysis: BloodworkAnalysis,
): boolean {
  // Fails closed: a panel still pending clinical review is held out of prod on
  // screen, and the PDF is the same content in the client's hands.
  if (!insightsPanelPrintable(section)) {
    return false;
  }
  switch (section) {
    // These two have printed whenever the section has an analysis to build from
    // since before the others existed, and they own their empty states:
    // AnemiaStatus always draws something, and Glucose is self-estimating, so
    // its height falls to zero on its own when the panel resolves to nothing.
    case Section.RBC:
    case Section.GLUCOSE:
      return true;
    // WBC does NOT own its empty state, which is why it is no longer grouped
    // with those two. WBCEvaluation renders null without a WBC value, while
    // this section's entry in EVALUATION_PANEL_HEIGHT is the last flat constant
    // in that table (300) and cannot fall to zero the way a self-estimating
    // panel's does. Answering "yes" for a panel that draws nothing therefore
    // reserves 300px of a page for it, and the section's results get pushed
    // onto a page of their own under a second copy of the title.
    //
    // Unreachable today: bloodwork-analysis-pdf.tsx only renders the Immune
    // section when a WBC value exists (showImmuneSection). But that is a
    // caller's guard standing in for this predicate's own correctness, and this
    // file's whole contract is that the render decision and the height are one
    // question asked once. Same question the screen asks in
    // BloodworkTestAnalysisSection.panelFor, and the same one render() answers.
    //
    // Asked of the analysis the panel will be BUILT from, so a section made
    // entirely of previous-only markers still prints that collection's gauge:
    // every caller passes the result of insightsSourceAnalysis.
    case Section.WBC:
      return !!bloodworkAnalysis?.wbc?.hasValue?.();
    case Section.NUTRIENTS:
      return NutrientInsights.shouldRender(bloodworkAnalysis);
    case Section.THYROID:
      return ThyroidRegulationInsights.shouldRender(bloodworkAnalysis);
    case Section.STRESS_ADRENAL:
      return StressAdrenalRegulationInsights.shouldRender(bloodworkAnalysis);
    case Section.ELECTROLYTES:
      return (
        DEHYDRATION_PANEL_ENABLED &&
        DehydrationInsights.shouldRender(bloodworkAnalysis)
      );
    default:
      return false;
  }
}

export class PDFSection extends React.Component<Props, {}> {
  furtherConsiderationsFor(
    rootCauseCategory: RootCauseCategory,
    section: string,
    tab: string,
  ): JSX.Element {
    const { furtherConsiderations } = this.props;
    return (
      <span key={rootCauseCategory}>
        {
          // tslint:disable-next-line:max-line-length
          furtherConsiderations
            .findConsiderations(rootCauseCategory, section, tab)
            .map((consideration, cindex) => {
              return (
                <span key={cindex}>
                  <span key={`title-${cindex}`} className="consideration-title">
                    {consideration.title}
                  </span>
                  <ul>
                    {consideration.items.map((item, itemIndex) => {
                      return (
                        <li key={`${rootCauseCategory}-${cindex}-${itemIndex}`}>
                          {item}
                        </li>
                      );
                    })}
                  </ul>
                </span>
              );
            })
        }
      </span>
    );
  }

/*
  preparedFor(bloodworkAnalysis: BloodworkAnalysis) {
    return (
      <div>
        <h6 className="prepared-for-container">
          <strong>Prepared for: </strong>
          <span>
            {bloodworkAnalysis.bloodwork.client.firstName}{' '}
            {bloodworkAnalysis.bloodwork.client.lastName}
          </span>
          <strong style={{ marginLeft: 20 }}>Collection Date: </strong>
          <span>
            <Moment format="MM/DD/YYYY">
              {bloodworkAnalysis.bloodwork.collectionDate}
            </Moment>
          </span>
        </h6>
      </div>
    );
  }
*/

  /**
   * The section's Insights panel, built from `bloodworkAnalysis`.
   *
   * That is the CURRENT analysis, except in a section made entirely of
   * previous-only markers, where it is the previous one and `fromPrevious`
   * captions the panel with that collection's date. Same rule as the on-screen
   * section (BloodworkTestAnalysisSection), because this document is the
   * client's copy of that screen.
   */
  evaluation(
    title: string,
    section: string,
    bloodworkAnalysis: BloodworkAnalysis,
    fromPrevious: boolean,
  ) {
    // Whether it prints at all is printsInsightsPanel's call, asked once so this
    // and the page-sizing in render() can never disagree. What is left here is
    // only WHICH component to build.
    if (!printsInsightsPanel(section as Section, bloodworkAnalysis)) {
      return null;
    }
    // RBC, WBC, and GLUCOSE all render an upper "Insights" panel followed by
    // their bar graphs. We rely on the per-section JSX in
    // bloodwork-analysis-pdf.tsx to push these sections onto a fresh page
    // (via the surrounding `<div className="html2pdf__page-break" />`), so we
    // do NOT add another empty break div here — adding one when the prior
    // section already forced a break results in a blank page slipping in.
    const panel =
      section === Section.RBC ? (
        <AnemiaStatus bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.WBC ? (
        <WBCEvaluation bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.GLUCOSE ? (
        <GlucoseRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.NUTRIENTS ? (
        // The Open Questions this panel prints are client-facing by decision:
        // they name the follow-up the practitioner would order, and the client
        // seeing them is what supports that conversation.
        <NutrientInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.THYROID ? (
        <ThyroidRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.STRESS_ADRENAL ? (
        // Added 2026-08-21 so the panel is not screen-only. The PDF is the
        // client's copy of the screen, so an insights panel the practitioner
        // sees on screen has to reach the document too — the env gate on the
        // screen side already decides whether it renders at all.
        <StressAdrenalRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : section === Section.ELECTROLYTES ? (
        <DehydrationInsights bloodworkAnalysis={bloodworkAnalysis} />
      ) : null;
    if (!panel) {
      return null;
    }
    return (
      <div>
        <h4 className="pdf-section-title"><strong>{title}</strong></h4>
        {this.furtherConsiderationsFor(
          RootCauseCategory.PDF,
          section,
          'Evaluation',
        )}
        <div className="pdf-anemia-status">
          {fromPrevious ? (
            <div className="previous-collection-insights">
              <div className="previous-collection-caption">
                {previousCollectionLabel(bloodworkAnalysis.bloodwork)}
              </div>
              {panel}
            </div>
          ) : (
            panel
          )}
        </div>
      </div>
    );
  }

  refRangeTitle(): string {
    const { section } = this.props;
    if (section === Section.WBC) {
      return 'Immune Activity Results';
    } else if (section === Section.PLATELETS) {
      return 'Blood Clotting & Flow Results';
    }
    else if (section === Section.PROTEINS) {
      return 'Protein Results';
    }
    else if (section === Section.NUTRIENTS) {
      return 'Vitamin & Mineral Results';
    }
    else if (section === Section.SEX_HORMONE) {
      return 'Hormone Results';
    }
    else if (section === Section.INFLAMMATION_OXIDATIVE_STRESS) {
      return 'Inflammation & Oxidative Stress Results';
    }
    else if (section === Section.LIPIDS) {
      return 'Lipid Results';
    }
    else if (section === Section.LIVER_BILIARY) {
      return 'Liver Results';
    }
    else if (section === Section.ELECTROLYTES) {
      return 'Electrolyte Results';
    }
    else if (section === Section.GLUCOSE) {
      return 'Glucose Regulation & Metabolic Function Results';
    }
    else if (section === Section.CUSTOM_MARKERS) {
      // A section the practitioner named is titled by that name — see the same
      // branch in tabs-component.
      if (this.props.practitionerSectionName) {
        return `${this.props.practitionerSectionName} Results`;
      }
      // The default would read "Other Lab Results Results".
      return Section.CUSTOM_MARKERS;
    }
    return `${section} Results`;
  }

  render() {
    const { bloodworkAnalysis, panelMarkers, title, section } = this.props;
    // Resolved here rather than passed in, for the same reason the screen
    // resolves it in tabs-component: a section is identified by the same two
    // values in both places, and threading a third marker list down through
    // every call site in bloodwork-analysis-pdf.tsx would be eighteen chances
    // to pass the wrong one.
    const textMarkers = this.props.hideTextMarkers
      ? []
      : sectionTextMarkers(
          bloodworkAnalysis,
          section,
          this.props.practitionerSectionName,
        );
    // A section can be entirely text — a urinalysis panel is exactly that — so
    // an empty bar-graph list is no longer an empty section.
    if (panelMarkers.length === 0 && textMarkers.length === 0) {
      return null;
    }
    // Per-section notes are no longer rendered here — they are consolidated into
    // the Section Notes panel near the report bottom (SectionNotesSummaryPanel),
    // so a long note no longer competes with this section's bar graphs for space
    // on the page.

    // The practitioner's own markers have no bibliography anchor to point at.
    const infoLink =
      section === Section.CUSTOM_MARKERS
        ? undefined
        : `https://labsmarts.com/bibliography/#${section}`;
    // Sections that render their own panel + title in evaluation() already
    // have the section title at the top of the page. The bar-graphs wrapper
    // below uses page-break-inside: avoid (via .pdf-glucose-bar-graphs) so
    // when the bar-graphs don't fit on the Insights page they move whole
    // to the next page. In that case we want the title shown above them on
    // the new page so the reader knows what they are looking at; when they
    // DO fit on the Insights page, the title would be a duplicate and we
    // hide it.
    //
    // A section built entirely from previous-only markers (values last test,
    // none this one) prints the PREVIOUS collection's panel, captioned with
    // that date — never a blank gauge built from the current test. With no
    // previous analysis to fall back on there is no panel at all, and the
    // title prints above the bar graphs like any other panel-less section.
    const insightsAnalysis = insightsSourceAnalysis(
      section,
      panelMarkers,
      bloodworkAnalysis,
    );
    const insightsFromPrevious =
      !!insightsAnalysis && insightsAnalysis !== bloodworkAnalysis;
    const hasEvaluationPanel =
      !!insightsAnalysis &&
      !this.props.hideInsightsPanel &&
      printsInsightsPanel(section, insightsAnalysis);
    // Layout estimates mirror the constants used by the PDF packer. We use
    // them to predict whether the bar-graphs wrapper will fit on the same
    // page as its Insights panel; if not, we keep the title.
    /*
     * The real page, not the packer's budget.
     *
     * A PDF page holds PDF_PAGE_BREAK_GRID_PX (1073) of captured content — see
     * the derivation beside PDF_CAPTURE_WIDTH_PX in bloodwork-analysis-pdf.tsx
     * — and a section opens with its own h4, worth about 40 of it. The packer
     * next door budgets pages at 900 on purpose, holding slack back so a
     * section never overflows; this test asks a different question ("do the
     * panel and the card fit together?") where that slack is simply a wrong
     * answer. At 900 it said no for Stress & Adrenal, whose panel and results
     * measure 871 together and sit on one page perfectly well — and a wrong no
     * prints the section heading twice.
     */
    const PAGE_CAPACITY = 1030;
    // The same measured pair the packer budgets with — see pdfPanelMetrics.
    // These were a separate local copy and had drifted from it.
    const BASE_HEIGHT = BAR_GRAPH_BASE_HEIGHT;
    const ROW_HEIGHT = BAR_GRAPH_ROW_HEIGHT;
    const SECTION_GAP = 24;
    const SAFETY_BUFFER = 60;
    // .pdf-glucose-bar-graphs adds padding-top to give the title a buffer
    // when the wrapper moves whole onto a new page; include it here so the
    // fit prediction matches the wrapper's actual rendered height.
    const KEEP_TOGETHER_PADDING_TOP = 16;
    // Estimates are tuned so that when the prediction says "fits", the bar
    // graphs really do render alongside the Insights panel on a single
    // page (and we hide the duplicate title), and when it says "doesn't
    // fit", html2pdf actually moves the bar-graphs wrapper to a new page
    // (and we keep the title at the top of that page).
    // The RBC Insights panel (AnemiaStatus) is not a fixed height — it grows
    // with each conditional finding it renders (nutrient-deficiency note, bone-
    // marrow overproduction, possible root-cause list, dehydration, poor
    // oxygenation, smoking). A single fixed estimate under-counts a panel full
    // of findings, so the predictor thinks the bar graphs fit beneath it and
    // hides the title — even though html2pdf actually pushes the Results onto a
    // fresh page, leaving them with no title. Estimate RBC from the same
    // signals AnemiaStatus uses so the prediction tracks the rendered height.
    //
    // Reads the analysis the panel is actually BUILT from, which for a
    // previous-only section is the previous one: estimating a printed panel
    // from a different test's findings is how the title-duplication predictor
    // goes wrong.
    const rbcEvaluationPanelHeight = (ba: BloodworkAnalysis) => {
      /*
       * MEASURED off the rendered panel at its real 300px width, in the same
       * capture pixels as everything else (see pdfPanelMetrics). A bare panel
       * measures 383: card chrome 93, the 200px gauge, and the size/colour row
       * at 90 with its 10px margin.
       *
       * The numbers here replace a set hand-tuned against an older page budget.
       * Once that budget was re-derived from the real page, the old set read
       * short and the fit test started believing the results still fitted under
       * the panel — which hid "Blood Oxygen & Red Blood Cell Function" above
       * them on the page html2pdf moved them to.
       */
      const LINE = 18;            // Bootstrap's 1.5 line-height at 12px
      const BOX = 10 + 16;        // a bordered box: 10px margin, 3px border, 5px padding
      let height = 93 + 200 + 10 + 90;
      if (ba.nutrientDeficiencyOrInflammationLikelyText) {
        height += BOX + LINE;
      }
      if (ba.boneMarrowAnalysis?.boneMarrowOverproductionIsLikely) {
        height += BOX + LINE;
      }
      const rootCauses = ba.possibleRootCauses?.() ?? [];
      if (rootCauses.length) {
        // Title line, then a line per cause — carried at two, because these
        // read as full sentences and wrap inside a 300px panel more often than
        // not ("Vitamin B12 Insufficiency Possibly Caused by Stomach Acid
        // Insufficiency" is three).
        height += BOX + LINE + rootCauses.length * 2 * LINE;
      }
      if (ba.isAnyDehydrationPossibleOrHigher?.()) {
        height += BOX + LINE;
      }
      if (ba.isPoorOxygenationPossibleOrHigher?.()) {
        height += BOX + LINE;
      }
      if (ba.isPoorOxygenationDueToSmoking?.()) {
        height += BOX + LINE;
      }
      return height;
    };
    const panelAnalysis = insightsAnalysis ?? bloodworkAnalysis;
    const EVALUATION_PANEL_HEIGHT: { [section: string]: number } = {
      [Section.RBC]: rbcEvaluationPanelHeight(panelAnalysis),
      [Section.WBC]: 300,      // WBCEvaluation + considerations
      // GlucoseRegulationInsights grows with every add-on pattern box it
      // resolves, so it estimates its own height (see
      // GlucoseRegulationInsights.estimatedPdfHeight) rather than carrying a
      // fixed number here. A standard single-pattern panel measures 280 — the
      // value this predictor was previously tuned to, which keeps the common
      // case unchanged: the full 8-base-marker glucose panel (glucose, HbA1c,
      // insulin, C-peptide, TG/HDL, TG, HDL, amylase) still fits under the
      // gauge with margin (280+24+516+60 = 880 <= 900) and the duplicate title
      // stays hidden. The same estimate feeds the PDF section packer, so the
      // two views of this panel's height can't drift apart.
      [Section.GLUCOSE]: GlucoseRegulationInsights.estimatedPdfHeight(panelAnalysis),
      // Same self-estimating approach as Glucose: this panel's height varies
      // with how many patterns and open questions resolve, so a fixed number
      // would mis-paginate every sparse panel (which is most of them).
      [Section.NUTRIENTS]: NutrientInsights.estimatedPdfHeight(panelAnalysis),
      // Both self-estimate for the same reason: they grow with the number of
      // pattern boxes / driver markers that resolve.
      [Section.THYROID]: ThyroidRegulationInsights.estimatedPdfHeight(panelAnalysis),
      [Section.STRESS_ADRENAL]:
        StressAdrenalRegulationInsights.estimatedPdfHeight(panelAnalysis),
      [Section.ELECTROLYTES]: DEHYDRATION_PANEL_ENABLED
        ? DehydrationInsights.estimatedPdfHeight(panelAnalysis)
        : 0,
    };
    // A text result prints as one table row rather than a chart, so it is
    // measured at a row's height — the SHARED constant, which the section packer
    // in bloodwork-analysis-pdf.tsx reads too. Left out, this predictor thinks
    // the bar graphs still fit under the Insights panel and hides the title
    // html2pdf then pushes onto a page of its own.
    const textBlockHeight = textMarkers.length
      ? textMarkers.length * BAR_GRAPH_TEXT_ROW_HEIGHT + BAR_GRAPH_TEXT_BLOCK_HEIGHT
      : 0;
    const barGraphsHeight =
      BASE_HEIGHT +
      panelMarkers.length * ROW_HEIGHT +
      textBlockHeight +
      KEEP_TOGETHER_PADDING_TOP;
    // Only ever read through hasEvaluationPanel, which has already settled
    // whether this build prints the panel (printsInsightsPanel), so a section
    // held back for clinical review never reaches this line.
    const evaluationPanelHeight = EVALUATION_PANEL_HEIGHT[section] ?? 0;
    // Zero means the panel renders nothing (an unresolved glucose gauge, say),
    // and a caption for a panel that isn't there costs no height.
    //
    // No "pending clinical review" notice is costed here any more: a panel
    // pending review is screen-only now, so a printing panel is by definition
    // a reviewed one and the notice never reaches the page.
    const evaluationHeight =
      evaluationPanelHeight > 0 && insightsFromPrevious
        ? evaluationPanelHeight + PREVIOUS_COLLECTION_CAPTION_PDF_HEIGHT
        : evaluationPanelHeight;
    const barGraphsFitOnEvaluationPage =
      hasEvaluationPanel &&
      evaluationHeight + SECTION_GAP + barGraphsHeight + SAFETY_BUFFER <= PAGE_CAPACITY;
    /*
     * ONE decision, used twice: the results either share the Insights page or
     * take the next one, and the heading follows that choice.
     *
     * They were two decisions before, and they disagreed. The title was shown
     * whenever the fit test said no, but nothing then made the break happen —
     * the wrapper's page-break-inside was the only mechanism, and this file's
     * own notes explain why that is unreliable. So the results printed a second
     * heading and then began under it on the SAME page, spilling the rest onto
     * the next: a duplicate heading above Stress & Adrenal's results, and
     * Vitamin & Mineral's bars split across two pages.
     *
     * An explicit break is deterministic where page-break-inside is not.
     */
    const barGraphsOnOwnPage = hasEvaluationPanel && !barGraphsFitOnEvaluationPage;
    const showBarGraphsTitle = !hasEvaluationPanel || barGraphsOnOwnPage;
    // Apply page-break-inside: avoid to sections whose bar graphs would
    // otherwise be split mid-table by html2pdf. RBC/WBC/GLUCOSE need it
    // because their bar-graphs wrapper must stay together with (or move
    // whole below) their Insights panel above. INFLAMMATION can have up
    // to 13 markers and gets close to the page-capacity ceiling — without
    // keep-together html2pdf tends to leave the title at the bottom of
    // the prior page and overflow the bar graphs onto the next one.
    //
    // The SET IS FIXED, and deliberately not "every section with a panel".
    // Widening it was an unintended side effect of teaching hasEvaluationPanel
    // about the newer panels: page-break-inside is the rule this file's own
    // notes call unreliable (it silently no-ops once the page-grid drift
    // exceeds a line, and it strands the rest of a page when it does fire), so
    // four more sections quietly acquired it and started leaving half-blank
    // pages. These four earned it by measurement over many exports; a new
    // section joins them the same way, not by having a panel.
    const KEEP_TOGETHER_SECTIONS: Section[] = [
      Section.RBC,
      Section.WBC,
      Section.GLUCOSE,
      Section.INFLAMMATION_OXIDATIVE_STRESS,
    ];
    const needsKeepTogether = KEEP_TOGETHER_SECTIONS.includes(section);
    const sectionWrapperClassName = needsKeepTogether ? 'pdf-glucose-bar-graphs' : '';
    return (
      <>
        {hasEvaluationPanel &&
          this.evaluation(title, section, panelAnalysis, insightsFromPrevious)}
        {barGraphsOnOwnPage && <div className="html2pdf__page-break" />}
        <div className={sectionWrapperClassName}>
          {showBarGraphsTitle && <h4 className="pdf-section-title"><strong>{title}</strong></h4>}
          {this.furtherConsiderationsFor(
            RootCauseCategory.PDF,
            section,
            'Analysis',
          )}
          <div className="pdf-ref-range-analysis">
            <ReferenceRangeAnalysisContainer
              title={this.refRangeTitle()}
              section={section}
              bloodwork={bloodworkAnalysis.bloodwork}
              previousBloodwork={bloodworkAnalysis.previousBloodwork}
              infoLink={infoLink}
              panelMarkers={panelMarkers}
              textMarkers={textMarkers}
              forPdf
            />
          </div>
        </div>
        {/* LAST, and it has to stay last: html2pdf measures each break in
            document order, so this one must be reached after the panel/results
            break above has already pushed the results onto their own page. */}
        {this.props.breakAfter && <div className="html2pdf__page-break" />}
        {/* <PDFTab
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Insights"
                    section={section}
                    tab="Interpretation"
                />                
                <PDFTab
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Additional Testing"
                    section={section}
                    tab="Testing"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Further Considerations"
                    section={section}
                    tab="Considerations"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Signs and Symptoms"
                    section={section}
                    tab="Symptoms"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Diet"
                    section={section}
                    tab="Diet"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Lifestyle"
                    section={section}
                    tab="Lifestyle"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Supplements"
                    section={section}
                    tab="Supplements"
                />
                */}
      </>
    );
  }
}
